import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as authApi from '@/api/auth.api';
import { registerAuthFailureHandler } from '@/api/client';
import { ApiError } from '@/api/errors';
import { tokenStorage } from '@/lib/storage';
import type { UserResponse } from '@/types/shared';

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: UserResponse | null;
  /** Throws `ApiError` on failure — callers (LoginPage) render the message per-case. */
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<UserResponse | null>(null);

  const clearSession = useCallback(() => {
    tokenStorage.clear();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Wired to the API client's refresh-on-401 flow: if a refresh ultimately fails (token
  // expired/reused per 07-cross-cutting.md §2), force a client-side logout.
  useEffect(() => {
    registerAuthFailureHandler(clearSession);
  }, [clearSession]);

  // On app boot: if tokens are already present, validate them against /auth/me rather than
  // trusting localStorage blindly (guards re-check isActive/role server-side on every request —
  // 07-cross-cutting.md §1).
  useEffect(() => {
    const hasTokens = Boolean(tokenStorage.getAccessToken() && tokenStorage.getRefreshToken());
    if (!hasTokens) {
      setStatus('unauthenticated');
      return;
    }

    authApi
      .me()
      .then((currentUser) => {
        setUser(currentUser);
        setStatus('authenticated');
      })
      .catch(() => {
        clearSession();
      });
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string, remember = false) => {
    try {
      const tokens = await authApi.login(email, password);
      tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken, remember);
      setUser(tokens.user);
      setStatus('authenticated');
    } catch (err) {
      throw ApiError.fromUnknown(err);
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch {
      // Best-effort: even if the network call fails, still clear the local session.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout }),
    [status, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
